import { http } from '@/app/lib/http/client'

export type StoreNavigationItemType = 'LINK' | 'CATEGORY_SELECTOR'
export type StoreNavigationTargetType = 'PRODUCT' | 'CATEGORY' | 'DEPARTMENT' | 'PRODUCT_TYPE' | 'PROMOTIONS' | 'URL'

export interface StoreNavigationItem {
  id: number
  menu_id: number
  label: string
  item_type: StoreNavigationItemType
  target_type: StoreNavigationTargetType | null
  target_id: number | null
  target_url: string | null
  promotion_id: number | null
  sort_order: number
  is_active: boolean
  created_at?: string
  updated_at?: string
}

export interface StoreNavigationMenu {
  id: number
  company: number
  name: string
  code: string
  is_active: boolean
  created_at?: string
  updated_at?: string
  items?: StoreNavigationItem[]
}

export interface Paginator<T> {
  current_page: number
  data: T[]
  from: number
  last_page: number
  per_page: number
  to: number
  total: number
}

export const navigationService = {
  // --- Menus ---
  async getMenus(params: { page?: number; per_page?: number; search?: string }): Promise<{ data: Paginator<StoreNavigationMenu> }> {
    const response = await http.get('/ecommerce/navigation', { params })
    return response.data
  },

  async getMenu(id: number): Promise<StoreNavigationMenu> {
    const response = await http.get(`/ecommerce/navigation/${id}`)
    return response.data.data
  },

  async createMenu(data: { name: string; code: string; is_active: boolean }): Promise<StoreNavigationMenu> {
    const response = await http.post('/ecommerce/navigation', data)
    return response.data.data
  },

  async updateMenu(id: number, data: { name?: string; code?: string; is_active?: boolean }): Promise<StoreNavigationMenu> {
    const response = await http.put(`/ecommerce/navigation/${id}`, data)
    return response.data.data
  },

  async toggleMenuStatus(id: number): Promise<void> {
    await http.patch(`/ecommerce/navigation/${id}/status`)
  },

  async deleteMenu(id: number): Promise<void> {
    await http.delete(`/ecommerce/navigation/${id}`)
  },

  // --- Items ---
  async createItem(menuId: number, data: Partial<StoreNavigationItem>): Promise<StoreNavigationItem> {
    const response = await http.post(`/ecommerce/navigation/${menuId}/items`, data)
    return response.data.data
  },

  async updateItem(menuId: number, itemId: number, data: Partial<StoreNavigationItem>): Promise<StoreNavigationItem> {
    const response = await http.put(`/ecommerce/navigation/${menuId}/items/${itemId}`, data)
    return response.data.data
  },

  async toggleItemStatus(menuId: number, itemId: number): Promise<void> {
    await http.patch(`/ecommerce/navigation/${menuId}/items/${itemId}/status`)
  },

  async deleteItem(menuId: number, itemId: number): Promise<void> {
    await http.delete(`/ecommerce/navigation/${menuId}/items/${itemId}`)
  },

  async reorderItems(menuId: number, items: { id: number; sort_order: number }[]): Promise<void> {
    await http.patch(`/ecommerce/navigation/${menuId}/items/reorder`, { items })
  },
}
